import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { pixelRatio2d } from '@/editor/math/const';
import type { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { EArrayPlaceMode, EEntityType, IArrayGridData, IBoxData } from '@/editor/types';
import { getUniqueId } from '@/editor/utils';
import * as THREE from 'three';
import { Line2, LineGeometry, LineMaterial, LineSegments2, LineSegmentsGeometry } from 'three/examples/jsm/Addons.js';
import { disposeAllChildren } from '../utils';
import BaseModel2d from './BaseModel2d';
import { IRulerInfo, RulerModel } from './rulerModel';

interface IArrayPreviewParams {
  data: IBoxData;
  mode: EArrayPlaceMode;
}

const currentColor = 0x1a7af8;
const previewColor = 0x00e1ff;
const dotColor = 0x262829;
const yColor = 0xdf4545;
const xColor = 0x66be74;
const dotBorderColor = 0xffffff;
const dotRadius = 0.04;
const dotBorderRadius = 0.06;
const axisLength = 1;
const axisArrowLength = 0.1;
const axisArrowWidth = 0.05;
const axisLabelSize = 0.4;
const axisLabelOffset = 0.16;
const dashedLineDashSize = 0.05;
const dashedLineGapSize = 0.025;
const dotSegments = 32;
const minSpacingRulerValue = 1e-6;

interface IAxisDirection {
  x: 1 | -1;
  y: 1 | -1;
}

interface IDotZOffset {
  border: number;
  dot: number;
}

interface ISpacingRulerAxisConfig {
  /** 标尺 id，同一次 updateModel 提交的各条标尺之间必须互异 */
  id: string;
  count: number;
  step: IPoint2d;
  size: number;
  spacing: number;
}

export default class ArrayPreviewHelper extends BaseModel2d {
  #sharedMaterials: THREE.Material[] = [];
  #currentMaterial: LineMaterial;
  #previewMaterial: LineMaterial;
  #dotMaterial: THREE.MeshBasicMaterial;
  #dotBorderMaterial: THREE.MeshBasicMaterial;
  #xLabelMaterial: THREE.MeshBasicMaterial;
  #yLabelMaterial: THREE.MeshBasicMaterial;
  #yMaterial: LineMaterial;
  #xMaterial: LineMaterial;
  #dashedLineMaterial: LineMaterial;
  #boxData: IBoxData;
  #xAxisGroup: THREE.Group | null = null;
  #yAxisGroup: THREE.Group | null = null;
  #startDotGroup: THREE.Group | null = null;
  #endDotGroup: THREE.Group | null = null;
  #gridData: IArrayGridData | null = null;
  #spacingRuler = new RulerModel();
  #zoom = 1;
  #mode: EArrayPlaceMode;

  constructor(params: IArrayPreviewParams) {
    super();
    this.#boxData = params.data;
    this.#mode = params.mode;

    this._modelGroup.userData.type = EEntityType.ArrayPreview;
    this._modelGroup.userData.entityId = getUniqueId();
    this.setDisplayOrder(ENTITY_RENDER_ORDER.arrayPreviewHelper);
    this.#spacingRuler.setDisplayOrder(ENTITY_RENDER_ORDER.arrayPreviewHelper + 1);

    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);

    this.#currentMaterial = this.#registerSharedMaterial(
      new LineMaterial({
        color: currentColor,
        depthTest: true,
        depthWrite: true,
        linewidth: 1.5
      })
    );

    this.#previewMaterial = this.#registerSharedMaterial(
      new LineMaterial({
        color: previewColor,
        depthTest: true,
        depthWrite: true,
        linewidth: 1.5
      })
    );

    this.#dotMaterial = this.#registerSharedMaterial(
      new THREE.MeshBasicMaterial({
        color: dotColor,
        depthTest: true,
        depthWrite: true
      })
    );

    this.#dotBorderMaterial = this.#registerSharedMaterial(
      new THREE.MeshBasicMaterial({
        color: dotBorderColor,
        depthTest: true,
        depthWrite: true
      })
    );

    this.#xLabelMaterial = this.#createLabelMaterial('X', xColor);
    this.#yLabelMaterial = this.#createLabelMaterial('Y', yColor);

    this.#yMaterial = this.#registerSharedMaterial(
      new LineMaterial({
        color: yColor,
        depthTest: true,
        depthWrite: true
      })
    );

    this.#xMaterial = this.#registerSharedMaterial(
      new LineMaterial({
        color: xColor,
        depthTest: true,
        depthWrite: true
      })
    );

    this.#dashedLineMaterial = this.#registerSharedMaterial(
      new LineMaterial({
        color: dotColor,
        depthTest: true,
        depthWrite: true,
        dashed: true,
        dashSize: dashedLineDashSize,
        gapSize: dashedLineGapSize
      })
    );

    this.#render();
  }

  public update(gridData: IArrayGridData) {
    this.#gridData = gridData;
    this.clear();
    this.#render();
  }

  #render() {
    this.#createCurrentBox();
    this.#createAxis();
    this.#createDot();
    this.#createDashedLine();
    this.#createPreview();
    this.#createSpacingRulers();
    this.#applyScreenInvariantScale();
  }

  #getZoomScale(zoom: number) {
    return 100 / Math.max(zoom, 1);
  }

  #applyScreenInvariantScale() {
    const scale = this.#getZoomScale(this.#zoom);
    this.#xAxisGroup?.scale.set(scale, scale, 1);
    this.#yAxisGroup?.scale.set(scale, scale, 1);
    this.#startDotGroup?.scale.set(scale, scale, 1);
    this.#endDotGroup?.scale.set(scale, scale, 1);
  }

  #createLine(scenePositions: number[], material: LineMaterial) {
    const line = new LineGeometry();
    line.setPositions(scenePositions);
    return new Line2(line, material);
  }

  #createMergedLineSegments(scenePositions: number[], material: LineMaterial) {
    const line = new LineSegmentsGeometry();
    line.setPositions(scenePositions);
    return new LineSegments2(line, material);
  }

  #registerSharedMaterial<T extends THREE.Material>(material: T) {
    material.userData.shared = true;
    this.#sharedMaterials.push(material);
    return material;
  }

  #disposeSharedResources() {
    for (const material of this.#sharedMaterials) {
      if (material instanceof THREE.MeshBasicMaterial && material.map) {
        material.map.dispose();
      }
      material.dispose();
    }

    this.#sharedMaterials = [];
  }

  #resetTransientGroups() {
    this.#xAxisGroup = null;
    this.#yAxisGroup = null;
    this.#startDotGroup = null;
    this.#endDotGroup = null;
  }

  #toSceneLength(value: number) {
    return value / pixelRatio2d;
  }

  #toScenePoint(point: IPoint2d, z = 0) {
    return new THREE.Vector3(this.#toSceneLength(point.x), this.#toSceneLength(point.y), z);
  }

  #toSceneSegmentPositions(start: IPoint2d, end: IPoint2d, z = 0) {
    return [
      this.#toSceneLength(start.x),
      this.#toSceneLength(start.y),
      z,
      this.#toSceneLength(end.x),
      this.#toSceneLength(end.y),
      z
    ];
  }

  #getSceneBoxSize() {
    return {
      width: this.#toSceneLength(this.#boxData.width),
      height: this.#toSceneLength(this.#boxData.thickness)
    };
  }

  #createSceneRectangleSegmentPositions(width: number, height: number) {
    return [
      -width / 2,
      -height / 2,
      0,
      width / 2,
      -height / 2,
      0,

      width / 2,
      -height / 2,
      0,
      width / 2,
      height / 2,
      0,

      width / 2,
      height / 2,
      0,
      -width / 2,
      height / 2,
      0,
      -width / 2,
      height / 2,
      0,
      -width / 2,
      -height / 2,
      0
    ];
  }

  #createBoxOutlineSegments(material: LineMaterial) {
    const { width, height } = this.#getSceneBoxSize();
    return this.#createMergedLineSegments(this.#createSceneRectangleSegmentPositions(width, height), material);
  }

  #offsetSceneSegmentPositions(scenePositions: number[], offset: THREE.Vector3) {
    const result = [...scenePositions];

    for (let i = 0; i < result.length; i += 3) {
      result[i] += offset.x;
      result[i + 1] += offset.y;
      result[i + 2] += offset.z;
    }

    return result;
  }

  #appendSceneRectangleSegments(targetScenePositions: number[], center: IPoint2d, rotation: number) {
    const { width, height } = this.#getSceneBoxSize();
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const sceneCenter = this.#toScenePoint(center);
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const corners = [
      { x: -halfWidth, y: -halfHeight },
      { x: halfWidth, y: -halfHeight },
      { x: halfWidth, y: halfHeight },
      { x: -halfWidth, y: halfHeight }
    ];

    for (let i = 0; i < corners.length; i++) {
      const start = corners[i];
      const end = corners[(i + 1) % corners.length];
      targetScenePositions.push(
        sceneCenter.x + start.x * cos - start.y * sin,
        sceneCenter.y + start.x * sin + start.y * cos,
        0,
        sceneCenter.x + end.x * cos - end.y * sin,
        sceneCenter.y + end.x * sin + end.y * cos,
        0
      );
    }
  }

  #applyAnchorTransform(target: THREE.Object3D) {
    const sceneCenter = this.#toScenePoint(this.#boxData.center);
    target.position.x = sceneCenter.x;
    target.position.y = sceneCenter.y;
    target.rotation.z = this.#boxData.rotation;
  }

  #createLabelMaterial(text: string, color: number) {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;

    const context = canvas.getContext('2d');
    if (!context) {
      return this.#registerSharedMaterial(new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 }));
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = '400 84px sans-serif';
    context.lineJoin = 'round';
    context.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    return this.#registerSharedMaterial(
      new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthTest: true,
        depthWrite: false
      })
    );
  }

  #createAxisLabel(material: THREE.MeshBasicMaterial, position: THREE.Vector3) {
    const label = new THREE.Mesh(new THREE.PlaneGeometry(axisLabelSize, axisLabelSize), material);
    label.position.copy(position);
    label.rotation.z = -this.#boxData.rotation;
    return label;
  }

  #createAxisGroup({
    material,
    labelMaterial,
    axisScenePositions,
    arrowPosition,
    arrowScenePositions,
    labelPosition
  }: {
    material: LineMaterial;
    labelMaterial: THREE.MeshBasicMaterial;
    axisScenePositions: number[];
    arrowPosition: THREE.Vector3;
    arrowScenePositions: number[][];
    labelPosition: THREE.Vector3;
  }) {
    const group = new THREE.Group();
    const mergedScenePositions = [...axisScenePositions];

    for (const scenePositions of arrowScenePositions) {
      mergedScenePositions.push(...this.#offsetSceneSegmentPositions(scenePositions, arrowPosition));
    }

    group.add(
      this.#createMergedLineSegments(mergedScenePositions, material),
      this.#createAxisLabel(labelMaterial, labelPosition)
    );

    return group;
  }

  #getAxisDirection(): IAxisDirection {
    if (!this.#gridData) {
      return { x: 1, y: 1 };
    }

    const { end } = this.#gridData;
    const endOffset = Point2dUtils.subtracted(end, this.#boxData.center);
    const localDirection =
      endOffset.x === 0 && endOffset.y === 0
        ? Point2dUtils.rotateDirection({ x: 1, y: 0 }, -this.#boxData.rotation)
        : Point2dUtils.rotateDirection(endOffset, -this.#boxData.rotation);

    return {
      x: localDirection.x >= 0 ? 1 : -1,
      y: localDirection.y >= 0 ? -1 : 1
    };
  }

  #createDotGroup(position: THREE.Vector3, zOffset: IDotZOffset) {
    const group = new THREE.Group();
    const border = new THREE.Mesh(new THREE.CircleGeometry(dotBorderRadius, dotSegments), this.#dotBorderMaterial);
    const dot = new THREE.Mesh(new THREE.CircleGeometry(dotRadius, dotSegments), this.#dotMaterial);

    group.position.copy(position);
    border.position.z = zOffset.border;
    dot.position.z = zOffset.dot;
    group.add(border, dot);

    return group;
  }

  #createCurrentBox() {
    const boxGroup = new THREE.Group();
    const border = this.#createBoxOutlineSegments(this.#currentMaterial);
    this.#applyAnchorTransform(boxGroup);
    boxGroup.add(border);
    this._modelGroup.add(boxGroup);
  }

  #createAxis() {
    if (this.#mode !== EArrayPlaceMode.Matrix) {
      return;
    }
    const { x: xDir, y: yDir } = this.#getAxisDirection();
    const yGroup = this.#createAxisGroup({
      material: this.#yMaterial,
      labelMaterial: this.#yLabelMaterial,
      axisScenePositions: [0, 0, 0, 0, -axisLength * yDir, 0],
      arrowPosition: new THREE.Vector3(0, -axisLength * yDir, 0),
      arrowScenePositions: [
        [0, 0, 0, -axisArrowWidth, axisArrowLength * yDir, 0],
        [0, 0, 0, axisArrowWidth, axisArrowLength * yDir, 0]
      ],
      labelPosition: new THREE.Vector3(-axisLabelSize * 0.65 * xDir, -axisLabelOffset * yDir, 0.005)
    });
    const xGroup = this.#createAxisGroup({
      material: this.#xMaterial,
      labelMaterial: this.#xLabelMaterial,
      axisScenePositions: [0, 0, 0, axisLength * xDir, 0, 0],
      arrowPosition: new THREE.Vector3(axisLength * xDir, 0, 0),
      arrowScenePositions: [
        [0, 0, 0, -axisArrowLength * xDir, axisArrowWidth, 0],
        [0, 0, 0, -axisArrowLength * xDir, -axisArrowWidth, 0]
      ],
      labelPosition: new THREE.Vector3(axisLabelOffset * xDir, axisLabelSize * 0.65 * yDir, 0.005)
    });

    this.#yAxisGroup = yGroup;
    this.#xAxisGroup = xGroup;

    this.#applyAnchorTransform(yGroup);
    this.#applyAnchorTransform(xGroup);

    this._modelGroup.add(yGroup);
    this._modelGroup.add(xGroup);
  }

  #createDot() {
    const startDot = this.#createDotGroup(this.#toScenePoint(this.#boxData.center, 0.01), { border: 0, dot: 0.001 });
    this.#startDotGroup = startDot;
    this._modelGroup.add(startDot);

    if (this.#gridData) {
      const endDot = this.#createDotGroup(this.#toScenePoint(this.#gridData.end, 0.01), {
        border: 0.001,
        dot: 0.01
      });
      this.#endDotGroup = endDot;
      this._modelGroup.add(endDot);
    }
  }

  #createDashedLine() {
    if (!this.#gridData) return;
    const { center: start } = this.#boxData;
    const { end } = this.#gridData;
    const dashedLine = this.#createLine(this.#toSceneSegmentPositions(start, end), this.#dashedLineMaterial);
    dashedLine.computeLineDistances();
    this._modelGroup.add(dashedLine);
  }

  #createPreview() {
    if (!this.#gridData) return;

    const { rotation } = this.#boxData;
    const { countX, countY, stepX, stepY } = this.#gridData;
    const previewPositions: number[] = [];

    for (let i = 0; i < countX; i++) {
      for (let j = 0; j < countY; j++) {
        if (i === 0 && j === 0) continue; // 跳过第一个，因为第一个是当前的实体预览
        const center = Point2dUtils.added(this.#boxData.center, {
          x: i * stepX.x + j * stepY.x,
          y: i * stepX.y + j * stepY.y
        });
        this.#appendSceneRectangleSegments(previewPositions, center, rotation);
      }
    }

    if (previewPositions.length > 0) {
      this._modelGroup.add(this.#createMergedLineSegments(previewPositions, this.#previewMaterial));
    }
  }

  #createSpacingRulers() {
    if (!this.#gridData) return;

    const { countX, countY, stepX, stepY, spacing } = this.#gridData;
    const rulerAxisConfigs: ISpacingRulerAxisConfig[] = [
      {
        id: 'x',
        count: countX,
        step: stepX,
        size: this.#boxData.width,
        spacing: spacing.x
      },
      {
        id: 'y',
        count: countY,
        step: stepY,
        size: this.#boxData.thickness,
        spacing: spacing.y
      }
    ];
    const rulerInfos = rulerAxisConfigs
      .map((config) => this.#createSpacingRulerInfo(config))
      .filter((rulerInfo): rulerInfo is IRulerInfo => !!rulerInfo);

    this.#spacingRuler.updateModel(rulerInfos);
  }

  #createSpacingRulerInfo({ id, count, step, size, spacing }: ISpacingRulerAxisConfig): IRulerInfo | null {
    if (count <= 1) return null;

    if (spacing <= minSpacingRulerValue) return null;

    const rulerDirection = Point2dUtils.getUnitDirection({ x: 0, y: 0 }, step);
    const previewCenter = Point2dUtils.added(this.#boxData.center, step);
    const start = Point2dUtils.addDistance(this.#boxData.center, size / 2, rulerDirection);
    const end = Point2dUtils.addDistance(previewCenter, -size / 2, rulerDirection);

    return { id, start, end, editable: false, offset: 0 };
  }

  public updateScale(zoom: number) {
    this.#zoom = zoom;
    this.#dashedLineMaterial.dashSize = (dashedLineDashSize / zoom) * 100;
    this.#dashedLineMaterial.gapSize = (dashedLineGapSize / zoom) * 100;
    this.#applyScreenInvariantScale();
  }

  public override clear() {
    disposeAllChildren(this._modelGroup);
    this.#spacingRuler.clear();
    this.#resetTransientGroups();
    this._modelGroup.scale.set(1, 1, 1);
  }

  public override dispose() {
    super.dispose();
    this.#spacingRuler.dispose();
    this.#resetTransientGroups();
    this.#disposeSharedResources();
  }
}
